import { useMemo } from 'react';
import { CallIntelligenceWritebackV1, CallIntelTypes } from '@frontend/api-call-intel';
import { useAppFlagStore } from '@frontend/shared';
import { CALL_INTEL_PMS_WRITEBACK_FF } from '../constants';
import { checkForPMSWritebackACL } from '../utils/call-intel-utils';

interface UseCallIntelPmsSyncConfigProps {
  call?: Pick<CallIntelTypes.CallRecord, 'person' | 'locationId' | 'id'>;
  fetchSyncStatus?: boolean;
  isDemo?: boolean;
}

const MAX_POLL_COUNT = 10;
const POLL_INTERVAL_MS = 10000; // 10 seconds

export const useCallIntelPmsSyncConfig = ({
  call,
  fetchSyncStatus = false,
  isDemo = false,
}: UseCallIntelPmsSyncConfigProps) => {
  const isContactPresent = !!call?.person?.id;
  const { getFeatureFlagValue } = useAppFlagStore();
  const selectedLocationIds = useMemo(() => (call?.locationId ? [call.locationId] : []), [call?.locationId]);

  const hasPmsSyncAcl = isDemo || checkForPMSWritebackACL({ locationId: call?.locationId || '' });

  const isPMSWritebackFFEnabled = getFeatureFlagValue({
    locationIds: selectedLocationIds,
    flagName: CALL_INTEL_PMS_WRITEBACK_FF,
  });

  const { data: summarySyncMode, isLoading: isLoadingSummarySyncMode } =
    CallIntelligenceWritebackV1.Queries.useGetWritebackCapabilitiesQuery({
      request: {
        locationIds: selectedLocationIds,
        isDemo,
      },
      options: {
        enabled: hasPmsSyncAcl && isPMSWritebackFFEnabled && selectedLocationIds.length > 0,
        select: (data) => {
          const isCapable = data.capabilities?.[0]?.capability?.isCapable;
          if (!isCapable) {
            return CallIntelligenceWritebackV1.Types.WritebackSettingMode.WRITEBACK_SETTING_MODE_OFF;
          }
          const writebackSetting = data.capabilities?.[0]?.settings?.find(
            ({ settingName }) =>
              settingName === CallIntelligenceWritebackV1.Types.WritebackSettingName.WRITEBACK_SETTING_CALL_SUMMARY
          );

          return writebackSetting?.mode;
        },
      },
    });

  const showSummarySync =
    hasPmsSyncAcl &&
    isPMSWritebackFFEnabled &&
    (isDemo ||
      !!(
        summarySyncMode &&
        summarySyncMode !== CallIntelligenceWritebackV1.Types.WritebackSettingMode.WRITEBACK_SETTING_MODE_OFF
      ));

  const {
    data: summarySyncStatusResponse,
    isLoading: isLoadingSummarySync,
    isFetching: isFetchingSummarySync,
  } = CallIntelligenceWritebackV1.Queries.useCallWritebackStatusV2Query({
    request: {
      callId: call?.id,
      // @ts-expect-error - isDemo is used internally by demo interceptor
      isDemo,
    },
    options: {
      enabled: hasPmsSyncAcl && showSummarySync && fetchSyncStatus && !!call?.id,
      refetchInterval: (data, query) => {
        if (!data?.callWritebacks) return false;

        const isIndeterminateStatus = data.callWritebacks?.some(({ status }) => {
          return (
            !!status &&
            [
              CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_INPROGRESS,
              CallIntelligenceWritebackV1.Types.WritebackStatus.WRITEBACK_STATUS_TRIGGERED,
            ].includes(status)
          );
        });

        if (isIndeterminateStatus && query.state.dataUpdateCount <= MAX_POLL_COUNT) {
          return POLL_INTERVAL_MS; // Poll every N seconds
        }

        // Stop polling if terminal status or max attempts reached
        return false;
      },
      refetchIntervalInBackground: false,
    },
  });

  return {
    isLoadingSummarySyncMode,
    summarySyncMode,
    isContactPresent,
    showSummarySync,
    isLoadingSummarySync,
    isFetchingSummarySync,
    summarySyncStatusResponse,
  };
};
